import * as Sentry from '@sentry/nextjs';
import {
  HydrationBoundary,
  QueryFunction,
  useMutation,
  useQueries,
  useQuery,
} from '@tanstack/react-query';
import dayjs from 'dayjs';
import Cookies from 'js-cookie';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';

import {
  createCart,
  deleteCart,
  getBrokerDetails,
  getEventTicketDetails,
  getTimeslots,
} from '@apiCalls';
import { CheckoutWrapper, TicketSelectorPanel } from '@components';
import {
  CART_ID_COOKIE,
  ESCROW_EXPIRY_MINUTES,
  SESSION_COOKIE_EXPIRATION,
} from '@constants';
import { useAppContext } from '@contexts';
import { currencyLookup, formatValue } from '@utils';
import { loadServerSideData } from '@utils/loadServerSideData';
import tracker, { InitTrackingParameters } from '@utils/tracking';

interface IExtendedTimeslot extends IEventTimeslotsResponse {
  ticketId: number;
}

const TicketSelect = ({
  event: eventProperty,
  eventSeries: eventSeriesProperty,
}: {
  event?: IEvent;
  eventSeries?: IEventSeries;
}) => {
  const router = useRouter();
  const {
    event,
    eventSeries,
    ticketDetails: { tickets: selectedTickets },
  } = useAppContext();
  const [totalAmount, setTotalAmount] = useState<number>();
  const [isTicketsListSuccess, setIsTicketsListSuccess] = useState(false);
  const paymentIntent = undefined;
  const purchasedTickets = undefined;
  const { data: broker } = useQuery({
    queryKey: ['broker', event.ID],
    queryFn: () => getBrokerDetails(event),
    enabled: Number.isFinite(event.ID) && event.ID > 0,
  });
  const handleContinue = () => {
    const repId =
      new URLSearchParams(window.location.search).get('repId') ?? '';
    const repIdParameter = repId ? `?repId=${repId}` : '';
    tracker.track({
      event: {
        event: 'Tickets Finalized',
        category: 'Checkout',
        action: 'Cart Created',
        value: eventSeries?.ID ?? event.ID,
      },
      platforms: ['ga'],
    });
    router.push(`/event/${event?.ID}/checkout/billing${repIdParameter}`);
  };

  useEffect(() => {
    tracker.track({
      event: 'view_item',
      GAparams: {
        items: [
          {
            item_id: event.ID,
            item_name: event.Name,
            item_brand: event.OrganisationName,
            item_category: 'Event',
            item_category2: 'Online',
          },
        ],
      },
      platforms: ['ga'],
    });
    if (eventSeriesProperty && !event.ID) {
      // loading ticket page without having an event selected first is not allowed
      Sentry.captureException(
        new Error('Incomplete data when loading Ticket selection'),
        {
          tags: {
            eventId: event.ID,
            eventProperty: eventProperty?.ID,
            eventSeriesId: eventSeriesProperty.ID,
          },
        }
      );
      // TODO: consider restoring when SSR vs sessions are sorted. Jotai Atom with storage is client-only
      // router.push(`/event/${router.query.eventId}`);
      return;
    }
  }, [eventProperty, eventSeriesProperty]);

  // TODO: check if timeslots are required once API allows for simple check

  useEffect(() => {
    const configs: InitTrackingParameters[] = [
      {
        level: 'platform',
        trackingIds: {
          TrackingGoogleID:
            process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_TRACKING_ID,
          TrackingMetaID: process.env.NEXT_PUBLIC_META_TRACKING_ID,
        },
      },
    ];
    if (event.ID !== 0 && event.Organisation) {
      configs.push({
        level: 'organisation',
        trackingIds: {
          TrackingGoogleID: event.Organisation.TrackingGoogleID,
          TrackingMetaID: event.Organisation.TrackingMetaID,
        },
      });
    }
    if (broker) {
      configs.push({
        level: 'broker',
        trackingIds: {
          TrackingGoogleID: broker.TrackingGoogleID,
          TrackingMetaID: broker.TrackingMetaID,
        },
      });
    }
    tracker.init(configs);
  }, [event, broker]);

  // FIXME causes initial black flash
  if (!event.ID) return <></>;

  return !paymentIntent && !purchasedTickets ? (
    <CheckoutWrapper
      blocked={selectedTickets?.length === 0 || !isTicketsListSuccess}
      buttonText={
        selectedTickets?.some(selectedTicket => selectedTicket.SalePrice > 0)
          ? `Pay Now (${currencyLookup(
              selectedTickets[0].Currency
            )}${formatValue(totalAmount)})`
          : 'Book Now'
      }
      onButtonClick={handleContinue}
    >
      <TicketListBlock
        onStatusChange={setIsTicketsListSuccess}
        onTotalAmountChange={setTotalAmount}
        queryKeyID={event.ID}
        queryFn={() => getEventTicketDetails(event.ID)}
      />
    </CheckoutWrapper>
  ) : (
    <CheckoutWrapper
      buttonText='Add Contact Details'
      onButtonClick={() =>
        router.replace(`/event/${event.ID || eventSeries?.ID}/checkout/contact`)
      }
    >
      <div className='appCheckoutStepHeading'>
        <h1 className='appCheckoutStepTitle'>Tickets</h1>
        <p className='appCheckoutStepDescription'>
          You have already purchased tickets. You will be redirected to add
          contact details. If you are not please click the button below.
        </p>
      </div>
    </CheckoutWrapper>
  );
};

interface TicketListBlockProperties {
  title?: string;
  description?: string;
  onTotalAmountChange?: (amount: number) => void;
  onStatusChange: (isSuccess: boolean) => void;
  queryKeyID: number;
  queryFn:
    | QueryFunction<ITicketSummary[] | ITicket[] | undefined, string[], never>
    | undefined;
}

export const TicketListBlock = ({
  title,
  description,
  onTotalAmountChange,
  onStatusChange,
  queryKeyID,
  queryFn,
}: TicketListBlockProperties) => {
  const {
    event,
    eventSeries,
    cartId,
    setCartId,
    setEscrowExpiryTime,
    timeslot: selectedTimeslot,
    ticketDetails: { tickets: selectedTickets },
  } = useAppContext();
  const [tickets, setTickets] = useState<ITicket[] | ITicketSummary[]>();
  const { data: ticketsData, status: ticketsStatus } = useQuery({
    queryKey: ['tickets', String(queryKeyID)],
    queryFn,
    enabled: (!!event && event.ID > 0) || (!!eventSeries && eventSeries.ID > 0),
  });
  const createCartMutation = useMutation({
    mutationFn: () => {
      if (!event?.ID) throw new Error('Cannot create cart without event');
      const repId =
        new URLSearchParams(window.location.search).get('repId') ?? '';
      const cartRequest: ICreateCartRequest = { EventId: event.ID };
      if (repId) cartRequest.StaffKey = repId;
      return createCart(cartRequest);
    },
    onError: () => {
      throw new Error('Failed to create cart');
    },
  });
  // Use stable ticket IDs as dependency to prevent infinite query loops
  const timeslotQueries = useMemo(
    () =>
      ticketsData?.map(ticket => {
        return {
          queryKey: ['timeslot', ticket.ID],
          queryFn: async () => {
            const timeslots = await getTimeslots(ticket.ID);
            return {
              ticketId: ticket.ID,
              Timeslots: timeslots?.Timeslots,
            } as IExtendedTimeslot;
          },
          enabled: !!selectedTimeslot,
        };
      }),
    [ticketsData?.map(t => t.ID).join(','), selectedTimeslot]
  );
  const timeslotResults = useQueries({ queries: timeslotQueries ?? [] });

  // Memoize timeslot statuses to prevent unstable dependency
  const timeslotStatuses = useMemo(
    () => timeslotResults.map(result => result.status).join(','),
    [timeslotResults.map(result => result.status).join(',')]
  );

  const sortedTickets = useMemo(() => {
    if (!ticketsData) return [];
    // Create shallow copy to avoid mutating original array
    return [...ticketsData]
      .sort((a, b) => a.Remaining - b.Remaining) // Remaining ascending
      .sort((a, b) => a.Order - b.Order) // Order ascending
      .sort((a, b) => (a.Available ? (b.Available ? 0 : -1) : 1)); // Available first
  }, [ticketsData]);

  const availableTickets = tickets
    ? tickets.filter(ticket => ticket.Available !== false)
    : [];
  const isAtLeastOneTicketAvailable =
    availableTickets && availableTickets.length > 0;

  const totalAmount = selectedTickets
    ? selectedTickets
        .map(ticket => {
          const updatedTicketData = ticketsData?.find(t => t.ID === ticket.ID);
          return {
            ...ticket,
            SalePrice: updatedTicketData?.SalePrice ?? ticket.SalePrice,
          };
        })
        .reduce((total, ticket) => total + ticket.SalePrice, 0)
    : 0;

  useEffect(() => {
    if (onTotalAmountChange) {
      onTotalAmountChange(totalAmount);
    }
  }, [totalAmount, onTotalAmountChange]);

  useEffect(() => {
    if (
      ticketsStatus === 'success' &&
      createCartMutation.status !== 'pending'
    ) {
      onStatusChange(true);
    } else {
      onStatusChange(false);
    }
  }, [ticketsStatus, createCartMutation]);

  useEffect(() => {
    if (
      (selectedTimeslot && timeslotResults.some(query => !query.isSuccess)) ||
      ticketsStatus !== 'success'
    ) {
      return;
    }
    if (selectedTimeslot && sortedTickets) {
      let fetchedTickets = [...sortedTickets];
      const fetchedTimeslotData = timeslotResults
        .filter(({ data }) => data !== undefined)
        .flatMap(({ data }) => data) as IExtendedTimeslot[];
      for (const fetchedTimeslot of fetchedTimeslotData) {
        // find the selected timeslot in the fetched timeslots
        const matchingTimeslot = fetchedTimeslot.Timeslots.find(
          timeslot =>
            timeslot.LocalStartTime === selectedTimeslot?.LocalStartTime &&
            timeslot.LocalEndTime === selectedTimeslot?.LocalEndTime
        );
        if (!matchingTimeslot) {
          // keep all tickets that are not timeslotted or match the selected timeslot
          fetchedTickets = fetchedTickets.filter(
            ticket =>
              (ticket.HasTimeslot && ticket.ID !== fetchedTimeslot.ticketId) ||
              !ticket.HasTimeslot
          );
        }
      }
      setTickets(fetchedTickets);
    } else {
      setTickets(sortedTickets);
    }
  }, [timeslotStatuses, ticketsStatus, selectedTimeslot, sortedTickets]);

  useEffect(() => {
    if (event.ID && cartId)
      deleteCart(cartId).catch(error => {
        console.error(error); // temporary debug
      });

    createCartMutation.mutate();
  }, [event.ID]);

  useEffect(() => {
    if (!createCartMutation.data?.Key) return;
    const cartId = createCartMutation.data.Key;
    setCartId(cartId);
    Cookies.set(CART_ID_COOKIE, cartId, { expires: SESSION_COOKIE_EXPIRATION });
    setEscrowExpiryTime(dayjs().add(ESCROW_EXPIRY_MINUTES, 'minutes').toDate());
  }, [createCartMutation.data]);

  if (!isAtLeastOneTicketAvailable) return <></>;

  return (
    <>
      <div className='appCheckoutStepHeading'>
        <h1 className='appCheckoutStepTitle'>{title ?? `Tickets`}</h1>
        {isAtLeastOneTicketAvailable && ticketsStatus !== 'error' && (
          <p className='appCheckoutStepDescription'>
            {description ??
              `Please choose a ticket type and confirm your order.`}
          </p>
        )}
      </div>
      {cartId && tickets ? (
        <>
          <TicketSelectorPanel availableTickets={tickets} />
        </>
      ) : (
        <p className='appCheckoutStepDescription'>
          Loading available tickets...
        </p>
      )}
      {ticketsStatus === 'error' && (
        <p className='appCheckoutStepDescription'>
          Failed to load available tickets. Please try again later.
        </p>
      )}
      {createCartMutation.isError && (
        <p className='appCheckoutStepDescription'>
          {createCartMutation.error.message === 'NoTicketsAvailable'
            ? 'Sorry, this ticket is unavailable now'
            : // : createCartMutation.error.message === 'NoPromoCodeAvailable'
              // ? 'Sorry, this promo code is not available anymore. Please try again.'
              'Failed to place order. Please try again later.'}
        </p>
      )}
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async context =>
  loadServerSideData(context);

export default function TicketSelectPageRoute({
  dehydratedState,
  event,
  eventSeries,
}: {
  event: IEvent;
  eventSeries?: IEventSeries;
  dehydratedState: any;
}) {
  return (
    <HydrationBoundary state={dehydratedState}>
      <TicketSelect event={event} eventSeries={eventSeries} />
    </HydrationBoundary>
  );
}
